import React, { useState, useRef, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Mail, MessageSquare, Gift, Settings, Plus, UserCog, Edit, ExternalLink, MoreVertical, Trash2 } from 'lucide-react';
import { AutomationActionNodeSubType } from '@frontend/app/types/globalTypes';
import { useAuth } from '@frontend/context/authContext';
import { Button } from '@/shadcn/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';
import { ActionOption, FlowNodeData } from '../types';
import { cn } from '@/shadcn/lib/utils';

interface ActionNodeV2Props {
  data: FlowNodeData;
}

const getActionIcon = (subType?: AutomationActionNodeSubType) => {
  switch (subType) {
    case AutomationActionNodeSubType.SEND_EMAIL:
      return <Mail className="h-4 w-4 text-white" />;
    case AutomationActionNodeSubType.HANDLE_PFA_BOOST:
      return <Gift className="h-4 w-4 text-white" />;
    case AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST:
      return <MessageSquare className="h-4 w-4 text-white" />;
    case AutomationActionNodeSubType.UPDATE_MEMBER_DATA:
      return <UserCog className="h-4 w-4 text-white" />;
    default:
      return <Settings className="h-4 w-4 text-white" />;
  }
};

const getActionLabel = (subType?: AutomationActionNodeSubType) => {
  switch (subType) {
    case AutomationActionNodeSubType.SEND_EMAIL:
      return 'Send Email';
    case AutomationActionNodeSubType.HANDLE_PFA_BOOST:
      return 'Apply Boost';
    case AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST:
      return 'Cashback Boost';
    case AutomationActionNodeSubType.UPDATE_MEMBER_DATA:
      return 'Update Member Data';
    default:
      return 'Action';
  }
};

const getActionDescription = (subType?: AutomationActionNodeSubType, metadata?: any, actionOptions?: ActionOption[]) => {
  switch (subType) {
    case AutomationActionNodeSubType.SEND_EMAIL:
      if (metadata?.templateId && actionOptions?.length > 0) {
        // Find template name from actionOptions
        const template = actionOptions[0]?.messageTemplates?.find((t) => t.id === metadata.templateId);
        return template?.displayName || 'Email template configured';
      }
      return 'Send an email to the member';
    case AutomationActionNodeSubType.HANDLE_PFA_BOOST:
      return metadata?.boostId ? `Boost ID: ${metadata.boostId}` : 'Apply performance boost';
    case AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST:
      return 'Apply cashback boost to member';
    case AutomationActionNodeSubType.UPDATE_MEMBER_DATA:
      if (metadata?.type && metadata?.fieldValue) {
        const actionType = metadata.type === 'TAG' ? 'Add Tag' : 'Add to Group';
        return `${actionType}: ${metadata.fieldValue}`;
      }
      return 'Update member data';
    default:
      return 'Perform an action';
  }
};

export const ActionNodeV2: React.FC<ActionNodeV2Props> = ({ data }) => {
  const { clientInfo } = useAuth();
  const {
    nodeId,
    subType,
    metadata,
    isEditing,
    isSelected,
    onNodeClick,
    onDeleteNode,
    actionOptions,
  } = data;

  const [showOptionsPopover, setShowOptionsPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(false);
    onDeleteNode?.(nodeId);
  };

  const handleOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(!showOptionsPopover);
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowOptionsPopover(false);
      }
    };

    if (showOptionsPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showOptionsPopover]);

  const actionSubType = subType as AutomationActionNodeSubType;
  const isConfigured = metadata && (
    (actionSubType === AutomationActionNodeSubType.SEND_EMAIL && metadata.templateId && metadata.resourceId !== undefined && metadata.fromPreviousSender !== undefined) ||
    (actionSubType === AutomationActionNodeSubType.HANDLE_PFA_BOOST && metadata.boostId && metadata.resourceId !== undefined && metadata.fromPreviousSender !== undefined) ||
    (actionSubType === AutomationActionNodeSubType.HANDLE_CASHBACK_BOOST && metadata.resourceId !== undefined && metadata.fromPreviousSender !== undefined) ||
    (actionSubType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA && metadata.type && metadata.fieldValue)
  );

  // Handle redirect to template in new tab for SEND_EMAIL actions
  const handleRedirectToTemplate = () => {
    if (actionSubType === AutomationActionNodeSubType.SEND_EMAIL && metadata?.templateId) {
      window.open(`/client/${clientInfo.id}/settings/messageTemplates?templateId=${metadata.templateId}`, '_blank');
    }
  };

  // Get template details for SEND_EMAIL actions
  const getTemplateDetails = () => {
    if (actionSubType !== AutomationActionNodeSubType.SEND_EMAIL || !metadata?.templateId || !actionOptions?.length) {
      return { name: 'No template selected', subject: 'No subject available' };
    }

    const template = actionOptions[0]?.messageTemplates?.find((t) => t.id === metadata.templateId);
    return {
      name: template?.displayName || `Template ${metadata.templateId}`,
      subject: template?.subject || 'No subject available'
    };
  };

  // Get sender info for SEND_EMAIL actions
  const getSenderInfo = () => {
    if (actionSubType !== AutomationActionNodeSubType.SEND_EMAIL) return '';

    if (metadata?.fromPreviousSender) {
      return 'Previous Sender';
    }
    if (metadata?.resourceId && actionOptions?.length > 0) {
      const resource = actionOptions[0]?.emailResources?.find((r) => r.id === metadata.resourceId);
      if (resource) {
        // EmailResourceData only has displayName, so we'll use that
        return resource.displayName || `Resource ${metadata.resourceId}`;
      }
    }
    return 'Unspecified';
  };

  const getUpdateMemberDataDetails = () => {
    if (!metadata || !metadata.type || !metadata.fieldValue) {
      return { action: 'No update configured', type: 'Type', value: 'Value' };
    }

    const actionType = metadata.type === 'TAG' ? 'Tag' : 'Group';
    const actionVerb = metadata.type === 'TAG' ? 'Add tag' : 'Add to group';

    return {
      action: `${actionVerb}: ${metadata.fieldValue}`,
      type: actionType,
      value: metadata.fieldValue
    };
  };

  return (
    <div className="relative">
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />

      {/* Main node container - Figma style */}
      <div className="flex flex-col items-start justify-center relative w-[342px] transition-all duration-200">
        {/* Node with border */}
        <div className={cn(
          'w-full bg-white border border-gray-200 rounded-2xl transition-all duration-200',
          isSelected && 'border-black border-2'
        )}
        >
          {/* Header Card */}
          <div
            className="w-full bg-white cursor-pointer rounded-t-2xl"
            onClick={handleNodeClick}
          >
            <div className="flex h-12 items-center gap-1 p-3 relative w-full">
              <div className="flex justify-between flex-1 self-stretch grow items-center relative">
                <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                  <div className="bg-purple-500 flex w-6 h-6 items-center justify-center relative rounded-lg">
                    {getActionIcon(subType as AutomationActionNodeSubType)}
                  </div>
                  <div className="relative w-fit font-medium text-gray-900 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                    {getActionLabel(subType as AutomationActionNodeSubType)}
                  </div>
                </div>
                <div className="inline-flex gap-2 flex-[0_0_auto] items-center relative">
                  <div ref={popoverRef}>
                    <Popover open={showOptionsPopover} onOpenChange={setShowOptionsPopover}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex flex-col w-6 h-6 items-center justify-center gap-2 px-2 py-0.5 bg-white rounded-lg hover:bg-gray-100"
                          onClick={handleOptionsClick}
                        >
                          <MoreVertical className="w-3 h-3 text-gray-600" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-1" align="end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={handleDelete}
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete Node
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Separator line */}
          <div className="w-full h-px bg-gray-200" />

          {/* Content Card */}
          <div className="w-full bg-white rounded-b-2xl overflow-hidden">
            <div className="flex flex-col items-start gap-3 px-3 py-3 relative w-full bg-white">
              {isConfigured ? (
                // Configured state - show details
                <div className="flex flex-col items-start justify-center gap-2 p-2 relative self-stretch w-full flex-[0_0_auto] bg-gray-100 rounded-2xl overflow-hidden">
                  {actionSubType === AutomationActionNodeSubType.SEND_EMAIL ? (
                    // SEND_EMAIL specific UI
                    <div className="items-start gap-1 px-3 py-2 self-stretch w-full flex-[0_0_auto] bg-white rounded-xl border border-solid border-gray-200">
                      <div className="flex flex-col items-start gap-1 relative flex-1 grow p-0">
                        <div className="flex justify-between self-stretch w-full items-center relative flex-[0_0_auto]">
                          <div className="relative w-fit font-medium text-gray-700 text-sm tracking-[0] leading-5 whitespace-nowrap">
                            {getTemplateDetails().name}
                          </div>
                          <div className="inline-flex items-center gap-1 relative flex-[0_0_auto]">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="flex-col w-6 h-6 items-center justify-center gap-2 p-2 rounded-md"
                              onClick={handleRedirectToTemplate}
                            >
                              <ExternalLink className="w-3 h-3 text-gray-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="flex-col w-6 h-6 items-center justify-center gap-2 p-2 rounded-md"
                              onClick={handleNodeClick}
                            >
                              <Edit className="w-3 h-3 text-gray-500" />
                            </Button>
                          </div>
                        </div>
                        <div className="relative self-stretch text-xs tracking-[0] leading-5">
                          <span className="font-medium text-gray-700">Subject: </span>
                          <span className="text-gray-500">{getTemplateDetails().subject}</span>
                        </div>
                      </div>
                    </div>
                  ) : actionSubType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA ? (
                    // UPDATE_MEMBER_DATA specific UI - matching the Figma design
                    <div className="items-start gap-1 px-3 py-2 self-stretch w-full flex-[0_0_auto] bg-white rounded-xl border border-solid border-gray-200">
                      <div className="flex flex-col items-start gap-1 relative flex-1 grow p-0">
                        <div className="flex justify-between self-stretch w-full items-center relative flex-[0_0_auto]">
                          <div className="relative w-fit font-medium text-gray-700 text-sm tracking-[0] leading-5 whitespace-nowrap">
                            {getUpdateMemberDataDetails().action}
                          </div>
                          <div className="inline-flex items-center gap-1 relative flex-[0_0_auto]">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="flex-col w-6 h-6 items-center justify-center gap-2 p-2 rounded-md"
                              onClick={handleNodeClick}
                            >
                              <Edit className="w-3 h-3 text-gray-500" />
                            </Button>
                          </div>
                        </div>
                        <div className="relative self-stretch text-xs tracking-[0] leading-5">
                          <span className="font-medium text-gray-700">{getUpdateMemberDataDetails().type}: </span>
                          <span className="text-gray-500">{getUpdateMemberDataDetails().value}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Other action types - use existing logic
                    <div className="bg-green-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-green-700 font-medium">Configured</span>
                      <p className="text-xs text-green-600 mt-1">
                        {getActionDescription(subType as AutomationActionNodeSubType, metadata, actionOptions)}
                      </p>
                      {actionSubType === AutomationActionNodeSubType.HANDLE_PFA_BOOST && metadata?.boostId && (
                        <div className="bg-gray-50 rounded-lg px-3 py-2 mt-2">
                          <p className="text-xs text-gray-600">Boost ID: {metadata.boostId}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* SEND_EMAIL specific sender info */}
                  {actionSubType === AutomationActionNodeSubType.SEND_EMAIL && (
                    <div className="relative w-fit text-xs tracking-[0] leading-3">
                      <span className="font-medium text-gray-700">Send from: </span>
                      <span className="text-gray-500">{getSenderInfo()}</span>
                    </div>
                  )}
                </div>
              ) : (
                // Not configured state
                <div className="flex items-center justify-between px-3 py-2 relative self-stretch w-full flex-[0_0_auto] bg-yellow-50 rounded-xl overflow-hidden">
                  <Button
                    variant="outline"
                    className="inline-flex flex-col items-center gap-2 px-2 py-0.5 relative flex-[0_0_auto] bg-white rounded-lg border border-dashed border-gray-300 hover:bg-gray-50"
                    onClick={handleNodeClick}
                  >
                    <div className="gap-2 inline-flex items-center relative flex-[0_0_auto]">
                      <Plus className="w-3 h-3 text-gray-600" />
                      <div className="relative w-fit mt-[-1.00px] font-medium text-gray-600 text-xs tracking-[0] leading-[1.5] whitespace-nowrap">
                        Configure Action
                      </div>
                    </div>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom connector with add button - positioned like DelayNodeV2 */}
        {isEditing && (
          <div className="relative w-full flex justify-center -mt-3 z-10">
            <Button
              size="sm"
              className="flex w-6 h-6 items-center justify-center p-0 bg-gray-600 rounded-full border-2 border-white hover:bg-gray-700 shadow-md"
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                data.onShowOptionBar?.(nodeId, {
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
              }}
            >
              <Plus className="w-3 h-3 text-white" />
            </Button>
          </div>
        )}
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />
    </div>
  );
};
